import * as config from './config';
import * as httpRequest from './httpRequest';
import * as localRunnerCache from './runnerFileCache';
import { ArgError } from '../errors';
import { getLogger } from './logger';
import { validateUserAccessKey, type InitializedUserWithAuth } from './testimServicesApi';
import { featureFlagsService } from './featureFlagsService';

const logger = getLogger('testim-custom-token');

let _serverToken: string | undefined;
let _serverTokenExp: number | undefined;
let _refreshToken: string | undefined;
let _projectId: string | null = null;
let _token: string | null = null;
let _userAccessKey: string | undefined;
let _gridAccessWhitelistedIp: string | undefined;
let _initializedFromCache: boolean;

const FIVE_MIN_IN_MS = 5 * 60 * 1000;

export async function init(projectId: string, token: string) {
    _projectId = projectId;
    _token = token;
    const tokenValue = await generateToken();
    return tokenValue;
}

export function initFromData(
    authData: InitializedUserWithAuth['authData'],
    optionsData: { projectId: string; token: string; userAccessKey?: string; publicIp: string | undefined; initializedFromCache: boolean },
) {
    _projectId = optionsData.projectId;
    _token = optionsData.token;
    _userAccessKey = optionsData.userAccessKey;
    _serverToken = authData.token;
    _serverTokenExp = getTokenExp(_serverToken);
    _refreshToken = authData.refreshToken;
    _gridAccessWhitelistedIp = optionsData.publicIp;
    _initializedFromCache = optionsData.initializedFromCache;
}

function getTokenV3(projectId = _projectId, token = _token) {
    return localRunnerCache.memoize(() => {
        logger.info('request to get cli token from server');
        return httpRequest.post<{ refreshToken: string; token: string; uid: string }>({
            url: `${config.SERVICES_HOST()}/auth/token`,
            body: { projectId, token },
        });
    }, 'getTokenV3', FIVE_MIN_IN_MS * 10, { projectId, token })();
}

async function refreshToken() {
    logger.info('request to refresh JWT cli token from server');
    const customToken = await httpRequest.post<{ token: string; refreshToken?: string }>({
        url: `${config.SERVICES_HOST()}/auth/refreshToken`,
        body: { token: _serverToken, refreshToken: _refreshToken },
    });
    _serverToken = customToken.token;
    _serverTokenExp = getTokenExp(_serverToken);
    if (customToken.refreshToken) {
        _refreshToken = customToken.refreshToken;
    }
    return _serverToken;
}

async function generateToken() {
    try {
        const customToken = await getTokenV3();
        logger.info('successfully get cli token from server');
        _serverToken = customToken.token;
        _serverTokenExp = getTokenExp(_serverToken);
        _refreshToken = customToken.refreshToken;
        return _serverToken;
    } catch (error) {
        if (error.message.includes('Unauthorized')) {
            throw new ArgError(
                'Error trying to retrieve CLI token. ' +
                'Your CLI token and project might not match. ' +
                'Please make sure to pass `--project` and `--token` that' +
                ' match to each other or make sure they match in your ~/.testim file.');
        }
        logger.error(`While trying to retrieve CLI token. caught error: ${error}`, { projectId: _projectId });
        throw new ArgError(`While trying to retrieve CLI token, caught error: ${error}`);
    }
}

function getTokenExp(token: string) {
    const jwtLib = require('jsonwebtoken');
    const jwt = jwtLib.decode(token);
    return jwt.exp * 1000;
}

export async function getCustomTokenV3() {
    if (!_serverTokenExp) {
        return generateToken();
    }

    const timeMargin = featureFlagsService.flags.accessTokenExpiryMargin.getValue();
    // Refresh the token earlier than needed, to give clickim a chance to have more time to run, as it can't refresh it itself.
    if (_serverTokenExp < (Date.now() + (4 * timeMargin))) {
        try {
            return await refreshToken();
        } catch (err) {
            logger.error('failed to refresh token, executing fallback', err);
            return generateToken();
        }
    }
    return _serverToken!;
}

export function getRefreshToken() {
    return _refreshToken;
}

export interface TokenUserData { uid: string }
export function getTokenV3UserData(): Partial<TokenUserData> {
    if (_serverToken) {
        const jwtLib = require('jsonwebtoken');
        const data = jwtLib.decode(_serverToken);
        return { uid: data.id};
    }
    return {};
}

export async function getUserAccessKey() {
    if (!_userAccessKey || !_projectId) {
        return undefined;
    }

    const isValid = await validateUserAccessKey({ userAccessKey: _userAccessKey, projectId: _projectId });
    if (isValid) {
        return _userAccessKey;
    }

    const msg = `user access key "${_userAccessKey}" is invalid for project "${_projectId}"`;
    throw new ArgError(msg);
}

export function gridAccessWhitelistedIp() {
    return _gridAccessWhitelistedIp;
}

export function wasInitializedFromCache() {
    return _initializedFromCache;
}
